import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";
import {
  DocumentPreviewLinkRequestParams,
  FieldSelectOption,
  TaskData,
  TranslationTenant,
} from "@/core/types/types";
import { fetchWithTimeout, processApiResponse } from "@/core/utils";

interface InitApiResponse extends TaskData {}

interface ResetApiResponse extends TaskData {}

interface UpdateProcessVariablesApiResponse {
  message: string;
}

const fetchOptions: object = {
  headers: {
    "Content-Type": "application/json",
  },
};

const camundaApi = {
  submit: async (formData: object): Promise<any> => {
    const formattedAnswers: { [key: string]: { value: string | unknown } } = {};

    for (let [key, value] of Object.entries(formData)) {
      formattedAnswers[key] = { value };
    }

    const body: string = JSON.stringify({
      variables: {
        ...formattedAnswers,
      },
    });

    const response: Response = await fetchWithTimeout("api/submit", {
      ...fetchOptions,
      method: "POST",
      body,
    });

    return processApiResponse(response);
  },

  init: async (config?: InitialisationConfig): Promise<InitApiResponse> => {
    const response: Response = await fetchWithTimeout("api/init", {
      ...fetchOptions,
      method: "POST",
      body: JSON.stringify(config),
    });

    return processApiResponse(response);
  },

  getVariablesForProcess: async (variables: string[]) => {
    const queryParams = new URLSearchParams({
      variableNames: variables.join(","),
    }).toString();

    const response: Response = await fetchWithTimeout(
      `api/getVariablesForProcess?${queryParams}`,
      {
        ...fetchOptions,
        method: "GET",
      }
    );

    return processApiResponse(response);
  },

  goToPreviousStep: async () => {
    const response: Response = await fetchWithTimeout("api/goToPreviousStep", {
      ...fetchOptions,
      method: "POST",
    });

    return processApiResponse(response);
  },

  refreshStep: async () => {
    const response: Response = await fetchWithTimeout("api/refreshStep", {
      ...fetchOptions,
      method: "GET",
    });

    return processApiResponse(response);
  },

  reset: async (): Promise<ResetApiResponse> => {
    const response: Response = await fetchWithTimeout("api/reset", {
      ...fetchOptions,
      method: "POST",
    });

    return processApiResponse(response);
  },

  flush: async (): Promise<void> => {
    const response: Response = await fetchWithTimeout("api/flush", {
      ...fetchOptions,
      method: "POST",
    });

    return processApiResponse(response);
  },

  syncSignature: async (process: string): Promise<void> => {
    const response: Response = await fetchWithTimeout(
      `api/syncSignature?processId=${process}`,
      {
        ...fetchOptions,
        method: "POST",
      }
    );

    return processApiResponse(response);
  },

  getCountries: async (
    language: string
  ): Promise<{ countries: FieldSelectOption[] }> => {
    const langQuery: URLSearchParams = new URLSearchParams({ language });
    const response: Response = await fetchWithTimeout(
      `api/getCountries?${langQuery}`,
      {
        ...fetchOptions,
        method: "GET",
      }
    );
    return processApiResponse(response);
  },

  getTranslations: async (
    locale: string,
    language: string,
    tenant: TranslationTenant
  ): Promise<{ translations: any }> => {
    const translationQuery: URLSearchParams = new URLSearchParams({
      locale,
      language,
      tenant,
    });
    const response: Response = await fetchWithTimeout(
      `api/MIE/getTranslations?${translationQuery}`,
      {
        ...fetchOptions,
        method: "GET",
      }
    );
    return processApiResponse(response);
  },

  uploadFiles: async (files: File[]) => {
    const body = new FormData();
    files.forEach((file) => body.append("files", file));

    const response: Response = await fetchWithTimeout("api/uploadFiles", {
      method: "POST",
      body,
    });
    return processApiResponse(response);
  },

  unsubscribe: async (process: string, utm_campaign?: string) => {
    const queryParams = new URLSearchParams({ process });

    if (utm_campaign) {
      queryParams.append("utm_campaign", utm_campaign);
    }

    return await fetchWithTimeout(`api/unsubscribe?${queryParams}`, {
      ...fetchOptions,
      method: "GET",
    });
  },

  updateProcessVariables: async (
    updates: object
  ): Promise<UpdateProcessVariablesApiResponse> => {
    const body = {
      updates,
    };

    const response: Response = await fetchWithTimeout(
      "api/updateProcessVariables",
      {
        ...fetchOptions,
        method: "POST",
        body: JSON.stringify(body),
      }
    );

    return processApiResponse(response);
  },

  addProcessVariable: async (
    variableName: string,
    value: string
  ): Promise<any> => {
    const body = JSON.stringify({
      variableName: variableName,
      value: value,
    });

    const response: Response = await fetch(`api/addProcessVariable`, {
      ...fetchOptions,
      method: "PUT",
      body,
    });

    return response.ok;
  },

  getDocumentPreview: async (body: DocumentPreviewLinkRequestParams) => {
    const response: Response = await fetchWithTimeout(
      `api/getDocumentPreview`,
      {
        ...fetchOptions,
        method: "POST",
        responseType: "application/pdf",
        body: JSON.stringify(body),
      }
    );
    return processApiResponse(response);
  },
};

export { camundaApi };
